import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Zap, CheckCircle2, XCircle } from "lucide-react";
import { Button, Skeleton } from "@finora/ui";
import type { ServiceEndpointDto } from "@finora/types";
import { api } from "../../lib/api";

export function ServiceEndpointsPage() {
  const navigate = useNavigate();
  const { data: endpoints, isLoading } = useQuery({
    queryKey: ["service-endpoints"],
    queryFn: () => api.listServiceEndpoints(),
  });

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/finora-admin")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Nazad na HAFiskal Admin
      </button>

      <div>
        <h1 className="text-xl font-semibold text-slate-900">Settings / Services</h1>
        <p className="text-sm text-slate-500">
          Eksterni servisi (n8n workflow-i) koje HAFiskal poziva - URL/timeout/retry/autentifikacija se
          menjaju ovde, bez izmene koda ili restarta API-ja.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        endpoints?.map((e) => <ServiceEndpointCard key={e.key} endpoint={e} />)
      )}
    </div>
  );
}

function ServiceEndpointCard({ endpoint }: { endpoint: ServiceEndpointDto }) {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState(endpoint.url);
  const [status, setStatus] = useState(endpoint.status);
  const [timeoutMs, setTimeoutMs] = useState(endpoint.timeoutMs);
  const [retryCount, setRetryCount] = useState(endpoint.retryCount);
  const [retryDelayMs, setRetryDelayMs] = useState(endpoint.retryDelayMs);
  const [authHeaderName, setAuthHeaderName] = useState(endpoint.authHeaderName ?? "");
  const [authHeaderValue, setAuthHeaderValue] = useState("");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["service-endpoints"] });

  const save = useMutation({
    mutationFn: () =>
      api.updateServiceEndpoint(endpoint.key, {
        url,
        status,
        timeoutMs,
        retryCount,
        retryDelayMs,
        authHeaderName: authHeaderName || null,
        ...(authHeaderValue ? { authHeaderValue } : {}),
      }),
    onSuccess: () => {
      invalidate();
      setAuthHeaderValue("");
    },
  });

  const test = useMutation({
    mutationFn: () => api.testServiceEndpointConnection(endpoint.key),
    onSuccess: invalidate,
  });

  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">{endpoint.name}</h2>
          <p className="text-xs text-slate-400">Ključ: {endpoint.key}</p>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            status === "ENABLED" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
          }`}
        >
          {status}
        </span>
      </div>

      <div className="grid gap-3">
        <div>
          <label className="mb-1 block text-xs text-slate-500">URL</label>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="h-9 w-full rounded-lg border border-border px-2 font-mono text-xs focus:border-brand focus:outline-none"
          />
        </div>

        <div className="grid grid-cols-4 gap-2">
          <div>
            <label className="mb-1 block text-xs text-slate-500">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as "ENABLED" | "DISABLED")}
              className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            >
              <option value="ENABLED">ENABLED</option>
              <option value="DISABLED">DISABLED</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">Timeout (ms)</label>
            <input
              type="number"
              value={timeoutMs}
              onChange={(e) => setTimeoutMs(Number(e.target.value))}
              className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">Retry (broj)</label>
            <input
              type="number"
              min={0}
              value={retryCount}
              onChange={(e) => setRetryCount(Number(e.target.value))}
              className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">Retry delay (ms)</label>
            <input
              type="number"
              min={0}
              value={retryDelayMs}
              onChange={(e) => setRetryDelayMs(Number(e.target.value))}
              className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-xs text-slate-500">Auth header naziv (opciono)</label>
            <input
              value={authHeaderName}
              onChange={(e) => setAuthHeaderName(e.target.value)}
              placeholder="npr. Authorization"
              className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">
              Auth header vrednost {endpoint.hasAuthHeaderValue && `(trenutno: ${endpoint.authHeaderValueMasked})`}
            </label>
            <input
              type="password"
              value={authHeaderValue}
              onChange={(e) => setAuthHeaderValue(e.target.value)}
              placeholder={endpoint.hasAuthHeaderValue ? "ostavi prazno da ne menjaš" : "npr. Bearer xxx"}
              className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
        <div className="text-xs text-slate-400">
          Poslednja izmena: {new Date(endpoint.updatedAt).toLocaleString("sr-RS")}
          {endpoint.updatedBy && <span> · korisnik {endpoint.updatedBy}</span>}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" disabled={test.isPending} onClick={() => test.mutate()}>
            <Zap className="h-3.5 w-3.5" aria-hidden />
            Test Connection
          </Button>
          <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
            Sačuvaj
          </Button>
        </div>
      </div>

      {test.data && (
        <div
          className={`mt-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
            test.data.success ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
          }`}
        >
          {test.data.success ? (
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <XCircle className="h-4 w-4 shrink-0" aria-hidden />
          )}
          <span>
            {test.data.success ? "SUCCESS" : "FAILED"}
            {test.data.httpStatus !== null && ` · HTTP ${test.data.httpStatus}`} · {test.data.responseMs}ms
            {test.data.error && ` · ${test.data.error}`}
          </span>
        </div>
      )}

      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-400">
        <span>
          Poslednji uspešan test:{" "}
          {endpoint.lastSuccessAt ? new Date(endpoint.lastSuccessAt).toLocaleString("sr-RS") : "—"}
        </span>
        <span>
          Poslednji neuspešan test:{" "}
          {endpoint.lastFailureAt ? new Date(endpoint.lastFailureAt).toLocaleString("sr-RS") : "—"}
        </span>
      </div>
    </div>
  );
}
